import DonateWidget from "@/components/DonateWidget";
import { Heart, ShieldCheck, GraduationCap, Users, Building2 } from "lucide-react";

const SUPPORTS = [
  { icon: Users, title: "A child's confidence", text: "Opportunity for families regardless of income." },
  { icon: ShieldCheck, title: "Safe learning spaces", text: "Well-equipped, structured environments for growth." },
  { icon: GraduationCap, title: "Trained facilitators", text: "Consistent instructors who deliver real outcomes." },
  { icon: Building2, title: "Real-world skills", text: "STEM, arts, athletics, media & career exposure." },
];

const GIFT_AREAS = [
  { title: "Afterschool", text: "Daily enrichment blending SEL, academics, and workforce readiness." },
  { title: "Summer & Faculty", text: "Fun, hands-on programs that prevent summer learning loss." },
  { title: "The Workforce Compound", text: "The incubator where all learning pathways meet and students prepare for real-world success." },
];

export default function Support() {
  return (
    <div data-testid="support-page">
      <section className="bg-brandnavy">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Support LP Foundation</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-black tracking-tight text-white max-w-3xl">
            Investing in confidence, structure, and opportunity.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
            When you support LP Foundation, you are not funding activities. You are funding a child's confidence, a safe
            structured environment, trained facilitators, and access to real-world skills and career exposure.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-black text-brandnavy">What your support makes possible</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Between the ages of 8 and 16, many students disengage from learning — not because they lack ability, but because
            it doesn't feel meaningful. We connect academic skills to hands-on experiences in aviation, music production,
            digital finance, film, and clothing design. When learning feels relevant, motivation returns.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {SUPPORTS.map((s) => (
              <div key={s.title} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                <s.icon className="h-6 w-6 text-accent" />
                <h3 className="mt-3 font-display font-bold text-brandnavy">{s.title}</h3>
                <p className="mt-1 text-sm text-slate-600">{s.text}</p>
              </div>
            ))}
          </div>

          <h3 className="mt-12 font-display text-xl font-black text-brandnavy">Your gift supports</h3>
          <div className="mt-5 space-y-3">
            {GIFT_AREAS.map((g) => (
              <div key={g.title} className="rounded-2xl bg-slate-50 border border-slate-100 p-5">
                <h4 className="font-semibold text-brandnavy">{g.title}</h4>
                <p className="mt-1 text-sm text-slate-600">{g.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:sticky lg:top-24" id="donate">
          <DonateWidget />
          <p className="mt-4 text-center text-sm text-slate-500">
            LP Foundation is a 501(c)(3) nonprofit. Everything we do is structured, measurable, and community-centered.
          </p>
        </div>
      </section>

      <section className="bg-orange-50">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-16 text-center">
          <Heart className="mx-auto h-8 w-8 text-accent fill-current" />
          <h2 className="mt-4 font-display text-2xl sm:text-3xl font-black text-brandnavy">
            Early exposure builds confidence. Confidence builds capability. Capability builds futures.
          </h2>
          <p className="mt-3 text-slate-600">We look forward to building something meaningful together.</p>
        </div>
      </section>
    </div>
  );
}
